import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { AppContext } from '../../App';
import { getSupplierSelectList } from '../../clients/supplier_client';
import { getProductSelectList } from '../../clients/product_client';
import { getSupplierCredits } from '../../clients/supplier_credit_client';
import {
  createQuoteManually,
  updateQuote,
  parseSupplierQuoteOcr,
} from '../../clients/quote_client';
import { getProjectSelectList } from '../../clients/project_client';
import { getUsersForOrder } from '../../clients/user_client';
import QuoteItemComponent from './QuoteItemComponent';
import QuotePdfUploadComponent from './QuotePdfUploadComponent';
import QuoteExcelUploadComponent from './QuoteExcelUploadComponent';
import QuoteOcrReviewStep from './QuoteOcrReviewStep';
import * as yup from 'yup';
import { Formik, useFormikContext } from 'formik';
import {
  Col,
  Row,
  Form,
  Alert,
  OverlayTrigger,
  Spinner,
  Tooltip,
} from 'react-bootstrap';
import { FiletypePdf, PencilFill, Plus, BoxSeam, Wallet2 } from 'react-bootstrap-icons';

import {
  resolveEntityId,
  showToast,
  updateObjectInObjectArray,
} from '../../config_and_helpers/helpers';
import { logInfo, logError } from '../../config_and_helpers/logger';
import {
  findUserOption,
  withOptionalNoUser,
} from '../../config_and_helpers/userDropdownOptions';
import RequiredAsteriskComponent from '../Generic/RequiredAsteriskComponent';
import CustomQuoteContextHook from './CustomQuoteContextHook';
import DropdownSelect from '../Generic/DropdownSelect';
import BudgetLineSelect from '../LabData/BudgetLineSelect';
import './QuoteComponentStyle.css';
import '../Glass/GlassStyle.css';
import NumberInput from '../Generic/NumberInput';
import {
  parseNumericInput,
  yupNumericField,
  yupNumericString,
} from '../../utils/numberInput';
import {
  buildQuoteFundingFields,
  formatQuoteApiErrorMessage,
  hasValidQuoteCreditFunding,
  QUOTE_FUNDING_REQUIRED_MESSAGE,
} from '../../utils/quoteFundingPayload';

// Import the shared global cache
import globalSupplierSelectCache, {
  invalidateSupplierSelectCache,
  invalidateProductSelectCache,
  getProductSelectInvalidatedAt,
  isSupplierSelectCacheInvalidated,
} from '../../config_and_helpers/supplier_cache';

/**
 * Returns the supplier id from a quote object.
 * Handles both API shapes: supplier as { id, name, ... } or as a raw id (number/string).
 * @param {Object} quoteObj - Quote object (list or detail).
 * @returns {string|number} Supplier id or empty string.
 */
const getSupplierIdFromQuote = quoteObj => {
  if (!quoteObj) return '';
  const s = quoteObj.supplier;
  if (s == null) return '';
  return typeof s === 'object' ? s.id : s;
};

const mapUserToOption = user => {
  if (!user || user.id == null || String(user.id).trim() === '') {
    return null;
  }

  const displayName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  const fallbackEmail = (user.email || '').trim();
  const fallbackUsername = (user.username || '').trim();
  const label =
    displayName ||
    fallbackEmail ||
    fallbackUsername ||
    `User ${String(user.id).trim()}`;

  return {
    value: user.id,
    label,
  };
};

/**
 * Frosted Procure-style choice card for the create-quote wizard.
 */
const QuoteWizardChoice = ({
  icon: Icon,
  title,
  description,
  selected = false,
  onClick,
  disabled = false,
}) => (
  <button
    type="button"
    className={`quote-wizard-card${selected ? ' is-selected' : ''}`}
    onClick={onClick}
    disabled={disabled}
    aria-pressed={selected}
  >
    <span className="quote-wizard-card__icon" aria-hidden="true">
      <Icon size={22} />
    </span>
    <span className="quote-wizard-card__content">
      <span className="quote-wizard-card__title">{title}</span>
      <span className="quote-wizard-card__desc">{description}</span>
    </span>
  </button>
);

/**
 * Wizard section shell — yellow glass panel inside neutral modal body.
 */
const QuoteWizardPanel = ({ step, title, subtitle, children, footer = null }) => (
  <div className="quote-wizard-panel">
    <div className="quote-wizard-panel__head">
      <span className="quote-wizard-panel__step">{step}</span>
      <h3 className="quote-wizard-panel__title">{title}</h3>
      {subtitle ? (
        <p className="quote-wizard-panel__subtitle">{subtitle}</p>
      ) : null}
    </div>
    <div className="quote-wizard-panel__body">{children}</div>
    {footer ? <div className="quote-wizard-panel__foot">{footer}</div> : null}
  </div>
);

/**
 * Back navigation for wizard stages.
 */
const QuoteWizardBackButton = ({ onClick, label }) => (
  <Button
    type="button"
    variant="outline-secondary"
    size="sm"
    className="quote-wizard-back-btn"
    onClick={onClick}
  >
    {label}
  </Button>
);

/**
 * Re-validates the quote form after each values change has rendered.
 *
 * Formik validates every `setFieldValue` against the values from the previous render,
 * so when a handler or effect sets several fields at once (the funding pickers, the
 * uniform currency switch) the last validation sees stale values and its errors stand
 * until the next edit.
 * Skips the first render so create mode keeps `validateOnMount` off.
 */
const RevalidateAfterValuesChange = () => {
  const { values, validateForm } = useFormikContext();
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    validateForm();
  }, [values, validateForm]);

  return null;
};

// Validation schema for each quote item
const itemSchema = yup.object().shape({
  quantity: yupNumericString({
    required: true,
    integerOnly: true,
    min: 1,
    message: 'Quantity must be a positive number',
  }),
  price: yupNumericString({
    required: true,
    message: 'Current price must be a valid number',
  }),
  discount: yupNumericField({
    required: false,
    min: 0,
    max: 100,
    typeError: 'Discount percentage must be a valid number',
  })
    .nullable()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue == null) return null;
      const parsed = parseNumericInput(originalValue);
      return parsed === null ? NaN : parsed;
    }),
  currency: yup.string().required('Currency is required'),
});

/**
 * Formik/Yup validation for the quote modal.
 * The quote file is optional on create and edit. A chosen file is still type-checked.
 * The from-file OCR wizard keeps its own PDF requirement at confirm time.
 */
const formSchema = yup.object().shape({
    items: yup.mixed().when('quoteType', {
      is: 'prepayment',
      then: () => yup.mixed(),
      otherwise: () => yup.array().of(itemSchema),
    }),
    supplier: yup.string().required('Supplier is required'),
    quoteType: yup
      .string()
      .oneOf(['products', 'prepayment'])
      .default('products'),
    prepaymentAmount: yup.string().when('quoteType', {
      is: 'prepayment',
      then: () =>
        yupNumericString({
          required: true,
          message: 'Prepayment amount must be a valid number',
        }),
      otherwise: schema => schema.notRequired().nullable(),
    }),
    prepaymentCurrency: yup
      .string()
      .when('quoteType', {
        is: 'prepayment',
        then: schema => schema.required('Prepayment currency is required'),
        otherwise: schema => schema.notRequired().nullable(),
      }),
    quoteFile: yup
      .mixed()
      .notRequired()
      .test(
        'fileType',
        'Unsupported file format. Only PDF, Word and Excel format is accepted.',
        value => {
          if (!value) return true; // Bypass the test if no file is uploaded
          return value.every(file => ['application/pdf'].includes(file.type));
        }
      ),
    demandRef: yup
      .string()
      .required('Demand reference is required'),
    fundingCreditId: yup.string().nullable(),
    budget: yup.string().nullable(),
  });

/**
 * Represents a QuoteModal component.
 * @constructor
 * @param {Object} props - The component props.
 * @param {Function} props.onSuccessfulSubmit - The callback function to be called on successful form submission.
 * @param {Object} props.quoteObj - The quote object.
 * @param {boolean} props.homeShowModal - Flag indicating whether the modal should be shown on the home page.
 * @param {Function} props.setHomeShowModal - The function to set the flag indicating whether the modal should be shown on the home page.
 * @param {Function} props.clearSearchValue - The function to clear the search value.
 * @param {boolean} props.disableEdit - Flag indicating whether editing is disabled.
 */

const QuoteModal = ({
  onSuccessfulSubmit,
  quoteObj,
  homeShowModal,
  setHomeShowModal,
  clearSearchValue,
  disableEdit,
  supplierSelectList,
  onEditButtonHover,
  isEditButtonLoading,
  projectId, // Optional project ID to associate quote with project
  onProjectUpdate, // Optional callback to refresh project data after quote creation/update
}) => {
  const LOG_CONTEXT = 'QuoteModal';
  // useContext fetching the token for communication with the server
  const { token } = useContext(AppContext);
  // Custom hook fetching the quotes via teh quote Context or falling back on generic data
  const { quotes, setQuotes } = CustomQuoteContextHook();

  // useRef to indicate when products are fetched
  const fetchingProductsRef = useRef(null);

  // useRef to track which supplier's products have been fetched
  const fetchedSupplierProductsRef = useRef(new Set());

  // State declarations for managing form data and UI state
  /**
   * Create-quote wizard: null until user picks Manual vs From file.
   * Edit mode (`quoteObj`) ignores this and always shows the full form.
   * @type {null|'manual'|'file'}
   */
  const [entryMethod, setEntryMethod] = useState(null);
  /**
   * Create-quote wizard: false until user confirms quote type (stage 1).
   * Edit mode bypasses the wizard (treated as form stage).
   */
  const [quoteTypeChosen, setQuoteTypeChosen] = useState(() => !!quoteObj);
  const [localSupplierSelectList, setLocalSupplierSelectList] = useState([]);
  const [supplier, setSupplier] = useState(() =>
    getSupplierIdFromQuote(quoteObj)
  );
  const [productSelectList, setProductSelectList] = useState([]);
  const [projectSelectList, setProjectSelectList] = useState([]);
  const [selectedProject, setSelectedProject] = useState(
    projectId ||
      (quoteObj?.project
        ? typeof quoteObj.project === 'object'
          ? quoteObj.project.id
          : quoteObj.project
        : null)
  );
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [userSelectList, setUserSelectList] = useState([]);
  const [selectedUser, setSelectedUser] = useState(
    quoteObj?.user?.id || null
  );
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [quoteFile, setQuoteFile] = useState(
    quoteObj ? quoteObj.quote_url : ''
  );
  const [supplierCredits, setSupplierCredits] = useState([]);
  const [isLoadingSupplierCredits, setIsLoadingSupplierCredits] =
    useState(false);
  const isPrepaymentExistingQuote =
    !!quoteObj &&
    quoteObj.prepayment_amount != null &&
    quoteObj.prepayment_currency != null &&
    (!quoteObj.items || quoteObj.items.length === 0);
  const initialItemsForFormik = (
    quoteObj && quoteObj.items
      ? quoteObj.items
      : [
          {
            quantity: '',
            price: '',
            discount: '',
            currency: '',
          },
        ]
  ).map(item => ({
    quantity: item.quantity || '',
    price: item.price || '',
    discount: item?.discount || '',
    currency: item.currency || '',
  }));
  const initialItemsRef = useRef(initialItemsForFormik);

  const [items, setItems] = useState(
    quoteObj && quoteObj.items
      ? () => {
          return quoteObj.items.map(item => ({
            quote_item_id: item.id,
            product: item.product.id,
            quantity: item.quantity,
            price: item.price || '',
            discount: item.discount || '',
            currency: item.currency || '',
          }));
        }
      : [
          {
            product: '',
            quantity: '',
            price: '',
            discount: '',
            currency: '',
          },
        ]
  );
  // State management checking if all items in the items array have selected products
  const [itemsHaveProducts, setItemsHaveProducts] = useState(false);
  // State management for if all quote items have a uniform currency
  const [isUniformCurrency, setIsUniformCurrency] = useState(false);
  // State management for the Form.Switch controlling for if to select a uniform currency for all items or not
  const [uniformCurrencyChecked, setUniformCurrencyChecked] = useState(false);
  // State management of the uniform currency value from the select list
  const [uniformCurrencyValue, setUniformCurrencyValue] = useState('');
  const [fileChanged, setFileChanged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOcrParsing, setIsOcrParsing] = useState(false);
  const [ocrParseError, setOcrParseError] = useState('');
  const [ocrReviewData, setOcrReviewData] = useState(null);
  /** PDF kept for attach on prepayment OCR create (and optional product OCR). */
  const [ocrSourceFile, setOcrSourceFile] = useState(null);
  const [ocrSubmitError, setOcrSubmitError] = useState('');
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);
  const [showModal, setShowModal] = useState(
    homeShowModal ? homeShowModal : false
  );

  const userDropdownOptions = useMemo(
    () => withOptionalNoUser(userSelectList),
    [userSelectList]
  );

  /** Clear OCR/file-parse UI when leaving the from-file wizard path. */
  useEffect(() => {
    if (entryMethod === 'file') return;
    setOcrReviewData(null);
    setOcrSourceFile(null);
    setOcrParseError('');
    setOcrSubmitError('');
    setIsOcrParsing(false);
  }, [entryMethod]);

  /** Keep wizard stage 1 flag aligned when switching create vs edit from parent. */
  useEffect(() => {
    setQuoteTypeChosen(!!quoteObj);
  }, [quoteObj]);

  // Function determining if all items are of uniform currency and if so, updates the uniform currency value
  const determineCurrencyUniformity = useCallback(() => {
    const firstCurrency = items[0]?.currency;

    // Return false if no items or first item has no currency
    if (!firstCurrency) {
      return false;
    }

    const isCurrencyUniform = items.every(
      item => item.currency === firstCurrency
    );

    if (isCurrencyUniform) {
      // Only update if the value is different to prevent unnecessary state updates
      setUniformCurrencyValue(prevValue => {
        if (prevValue !== firstCurrency) {
          return firstCurrency;
        }
        return prevValue;
      });
    }

    return isCurrencyUniform;
  }, [items]);

  useEffect(() => {
    if (isUniformCurrency) {
      return;
    }

    if (items.length > 1) {
      const result = determineCurrencyUniformity();
      setIsUniformCurrency(result);
      if (result === true && quoteObj) setUniformCurrencyChecked(true);
    } else if (uniformCurrencyChecked) {
      setIsUniformCurrency(true);
    } else {
    }
  }, [
    items,
    uniformCurrencyChecked,
    determineCurrencyUniformity,
    isUniformCurrency,
    quoteObj,
  ]);

  // useEffect checking if any items do not have products selected, which disables the submit button
  useEffect(() => {
    const hasEmptyProduct = items.some(item => item.product === '');
    if (hasEmptyProduct) {
      setItemsHaveProducts(false);
    } else {
      setItemsHaveProducts(true);
    }
  }, [items]);

  // Function to fetch supplier select list on hover
  const fetchSupplierSelectList = useCallback(() => {
    // When cache was invalidated (e.g. after Excel/PDF created suppliers), refetch instead of using stale prop/cache
    if (!isSupplierSelectCacheInvalidated()) {
      if (supplierSelectList && supplierSelectList.length > 0) {
        setLocalSupplierSelectList(supplierSelectList);
        return;
      }
      if (supplierSelectList !== null) {
        setLocalSupplierSelectList([]);
        return;
      }
      // Check if cache exists and hasn't been invalidated
      const cacheIsValid =
        globalSupplierSelectCache.data &&
        (!globalSupplierSelectCache.invalidatedAt ||
          !globalSupplierSelectCache.timestamp ||
          globalSupplierSelectCache.timestamp >
            globalSupplierSelectCache.invalidatedAt);

      if (cacheIsValid) {
        setLocalSupplierSelectList(globalSupplierSelectCache.data);
        return;
      }
    }

    if (
      globalSupplierSelectCache.isFetching &&
      globalSupplierSelectCache.promise
    ) {
      setIsLoadingSuppliers(true);
      globalSupplierSelectCache.promise.then(() => {
        setLocalSupplierSelectList(globalSupplierSelectCache.data);
        setIsLoadingSuppliers(false);
      });
      return;
    }

    setIsLoadingSuppliers(true);
    globalSupplierSelectCache.isFetching = true;
    globalSupplierSelectCache.promise = getSupplierSelectList(token, data => {
      globalSupplierSelectCache.data = data;
      globalSupplierSelectCache.timestamp = Date.now();
      globalSupplierSelectCache.isFetching = false;
      setLocalSupplierSelectList(data);
      setIsLoadingSuppliers(false);
    });

    globalSupplierSelectCache.promise.then(() => {
      globalSupplierSelectCache.isFetching = false;
    });
  }, [token, supplierSelectList]);

  // Use supplier select list from props if provided, otherwise fetch it when modal is opened
  useEffect(() => {
    if (!showModal) return; // Only fetch when modal is opened
    fetchSupplierSelectList();
  }, [showModal, fetchSupplierSelectList]);

  // Fetch project select list when modal opens
  useEffect(() => {
    if (showModal && projectSelectList.length === 0 && !isLoadingProjects) {
      setIsLoadingProjects(true);
      getProjectSelectList(token, data => {
        if (data && Array.isArray(data)) {
          setProjectSelectList(data);
        } else {
          setProjectSelectList([]);
        }
        setIsLoadingProjects(false);
      }).catch(error => {
        setProjectSelectList([]);
        setIsLoadingProjects(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, token]);

  // Fetch supplier credits for the currently selected supplier when modal opens or supplier changes
  useEffect(() => {
    if (!showModal || !supplier) {
      setSupplierCredits([]);
      return;
    }
    let isCancelled = false;
    setIsLoadingSupplierCredits(true);
    getSupplierCredits(token, supplier)
      .then(credits => {
        if (isCancelled) return;
        setSupplierCredits(Array.isArray(credits) ? credits : []);
      })
      .catch(() => {})
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingSupplierCredits(false);
        }
      });
    return () => {
      isCancelled = true;
    };
  }, [showModal, supplier, token]);

  // Fetch users for quote when modal opens
  useEffect(() => {
    if (showModal && token) {
      setIsLoadingUsers(true);
      getUsersForOrder(token)
        .then(users => {
          if (Array.isArray(users) && !users.error) {
            const droppedUsers = [];
            const formattedUsers = users.reduce((acc, user) => {
              const mappedOption = mapUserToOption(user);
              if (mappedOption) {
                acc.push(mappedOption);
              } else {
                droppedUsers.push({
                  id: user?.id ?? null,
                  first_name: user?.first_name ?? null,
                  last_name: user?.last_name ?? null,
                  email: user?.email ?? null,
                  username: user?.username ?? null,
                });
              }
              return acc;
            }, []);

            if (process.env.NODE_ENV === 'development' && droppedUsers.length > 0) {
              logInfo(
                LOG_CONTEXT,
                'Dropped invalid users while building quote user options',
                {
                  droppedCount: droppedUsers.length,
                  droppedUsers,
                }
              );
            }

            setUserSelectList(formattedUsers);
          } else {
            setUserSelectList([]);
          }
          setIsLoadingUsers(false);
        })
        .catch(() => {
          setUserSelectList([]);
          setIsLoadingUsers(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, token]);

  // Reset selected project and user when modal closes; sync from quoteObj when modal opens
  useEffect(() => {
    if (!showModal) {
      setSelectedProject(null);
      setProjectSelectList([]);
      setSelectedUser(null);
    } else {
      if (projectId) {
        setSelectedProject(projectId);
      } else if (quoteObj?.project) {
        const projectValue =
          typeof quoteObj.project === 'object'
            ? quoteObj.project.id
            : quoteObj.project;
        setSelectedProject(projectValue);
      }
      const userId = quoteObj?.user != null
        ? (typeof quoteObj.user === 'object' ? quoteObj.user.id : quoteObj.user)
        : null;
      setSelectedUser(userId);
      // Sync supplier from quoteObj when opening edit so list quote shape (object vs id) is handled
      if (quoteObj) {
        setSupplier(getSupplierIdFromQuote(quoteObj));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, projectId, quoteObj, quoteObj?.project, quoteObj?.user]);

  // Update items when quoteObj changes (for lazy loading)
  useEffect(() => {
    if (quoteObj && quoteObj.items) {
      setItems(
        quoteObj.items.map(item => ({
          quote_item_id: item.id,
          product: item.product.id,
          quantity: item.quantity,
          price: item.price || '',
          discount: item.discount || '',
          currency: item.currency || '',
        }))
      );
      initialItemsRef.current = quoteObj.items.map(item => ({
        quantity: item.quantity || '',
        price: item.price || '',
        discount: item.discount || '',
        currency: item.currency || '',
      }));
    }
  }, [quoteObj]);

  // When opening edit modal, clear product fetch ref if product list was invalidated (e.g. after Excel upload)
  useEffect(() => {
    if (showModal && quoteObj && getProductSelectInvalidatedAt()) {
      fetchedSupplierProductsRef.current.clear();
    }
  }, [showModal, quoteObj]);

  // In edit mode, ensure the quote's supplier is in the dropdown (e.g. API returns limited list after Excel/PDF create)
  useEffect(() => {
    if (!showModal || !quoteObj || !supplier) return;
    const supplierId = getSupplierIdFromQuote(quoteObj);
    if (!supplierId) return;
    const alreadyInList = localSupplierSelectList.some(
      p =>
        p.value === supplierId ||
        String(p.value) === String(supplierId)
    );
    if (alreadyInList) return;
    const s = quoteObj.supplier;
    const label =
      typeof s === 'object' && s != null && 'name' in s
        ? s.name
        : String(supplierId);
    const option = { value: supplierId, label };
    setLocalSupplierSelectList(prev => [option, ...prev]);
  }, [
    showModal,
    quoteObj,
    supplier,
    localSupplierSelectList,
  ]);

  // Functions for fetching product list and managing state updates
  const fetchProductSelectList = useCallback(() => {
    const key = supplier != null ? String(supplier) : '';
    if (key && !fetchedSupplierProductsRef.current.has(key)) {
      fetchingProductsRef.current = true;
      getProductSelectList(token, setProductSelectList, supplier).then(() => {
        fetchingProductsRef.current = false;
        fetchedSupplierProductsRef.current.add(key);
      });
    }
  }, [token, supplier, setProductSelectList]);

  // Function to fetch products on hover (for editing quotes)
  const fetchProductsOnHover = useCallback(() => {
    const supplierId = quoteObj ? getSupplierIdFromQuote(quoteObj) : '';
    if (supplierId) {
      const key = String(supplierId);
      if (!fetchedSupplierProductsRef.current.has(key)) {
        fetchingProductsRef.current = true;
        getProductSelectList(token, setProductSelectList, supplierId).then(
          () => {
            fetchingProductsRef.current = false;
            fetchedSupplierProductsRef.current.add(key);
          }
        );
      }
    }
  }, [token, quoteObj, setProductSelectList]);

  useEffect(() => {
    if (productSelectList.length && items.length === 0) {
      setItems([
        { product: '', quantity: '', price: '', discount: '', currency: '' },
      ]);
    }
  }, [productSelectList, items.length]);

  // Only fetch products when supplier changes (for new quotes), not when editing existing quotes
  useEffect(() => {
    if (supplier && !quoteObj) {
      if (fetchedSupplierProductsRef.current.has(String(supplier))) {
        return;
      }

      // Only fetch products for new quotes when supplier changes
      fetchProductSelectList();
    }
  }, [supplier, fetchProductSelectList, quoteObj]);

  // Fetch products when edit modal opens if we have a supplier and haven't fetched yet (e.g. user clicked without hovering)
  // Use quoteObj to get supplier id so we don't depend on supplier state (which may not be updated yet in the same commit)
  useEffect(() => {
    if (!showModal || !quoteObj) return;
    const supplierId = getSupplierIdFromQuote(quoteObj);
    if (!supplierId) return;
    const key = String(supplierId);
    if (fetchedSupplierProductsRef.current.has(key)) return;
    fetchingProductsRef.current = true;
    getProductSelectList(token, setProductSelectList, supplierId).then(() => {
      fetchingProductsRef.current = false;
      fetchedSupplierProductsRef.current.add(key);
    });
  }, [showModal, quoteObj, token]);

  // Closes the modal and resets the form state
  const handleClose = () => {
    // Conditionally update the modal state in the parent component if applicable
    if (setHomeShowModal) setHomeShowModal(false);
    resetModal();
    setEntryMethod(null);
    setQuoteTypeChosen(!!quoteObj);
    setShowModal(false);
    setIsUniformCurrency(false);
    setUniformCurrencyChecked(false);
    setUniformCurrencyValue('');
  };

  // Resets the modal fields to their default values
  const resetModal = () => {
    // Only reset if creating a new quote (i.e., quoteObj is not provided)
    if (!quoteObj) {
      setSupplier('');
      setProductSelectList([]);
      setQuoteFile('');
      setItems([
        { product: '', quantity: '', price: '', discount: '', currency: '' },
      ]);
      setIsOcrParsing(false);
      setOcrParseError('');
      setOcrReviewData(null);
      setOcrSourceFile(null);
      setOcrSubmitError('');
      setEntryMethod(null);
      setQuoteTypeChosen(false);
      // Clear cached supplier products when creating a new quote
      fetchedSupplierProductsRef.current.clear();
    }
  };

  /**
   * Prepayment wizard: parse PDF via `parse_supplier_quote_ocr` with `mode=prepayment`.
   *
   * @param {File[]} files - Selected PDF(s); first file is used.
   */
  const handlePrepaymentOcrHandoff = async files => {
    const fileList = Array.isArray(files) ? files : [];
    const firstFile = fileList[0];
    if (!firstFile) return;
    setOcrSourceFile(firstFile);
    setOcrReviewData(null);
    setIsOcrParsing(true);
    setOcrParseError('');
    setOcrSubmitError('');
    logInfo(LOG_CONTEXT, 'Prepayment OCR parse stage', {
      phase: 'start',
      fileName: firstFile.name,
    });
    try {
      const result = await parseSupplierQuoteOcr(token, firstFile, {
        mode: 'prepayment',
      });
      if (result.success && result.data) {
        logInfo(LOG_CONTEXT, 'Prepayment OCR parse stage', {
          phase: 'success',
          fileName: firstFile.name,
          mode: result.data.mode,
          requiresManualReview: result.data.requires_manual_review === true,
        });
        setOcrReviewData(result.data);
      } else {
        logError(LOG_CONTEXT, 'Prepayment OCR parse stage failed', {
          phase: 'failed_response',
          fileName: firstFile.name,
          message: result?.message || null,
        });
        setOcrParseError(
          result.message || 'Failed to parse prepayment quote OCR payload.'
        );
      }
    } catch (err) {
      logError(LOG_CONTEXT, 'Prepayment OCR parse stage error', {
        phase: 'error',
        fileName: firstFile.name,
        message: err?.message || null,
      });
      setOcrParseError(err?.message || 'Failed to parse prepayment quote OCR.');
    } finally {
      setIsOcrParsing(false);
    }
  };

  /**
   * Create quote from OCR review: prepayment (`items: []` + PDF) or products (line items).
   *
   * @param {Object} createPayload - From `QuoteOcrReviewStep` confirm handler.
   */
  const handleConfirmOcrReview = async createPayload => {
    if (!token) return;
    setIsSubmitting(true);
    setOcrSubmitError('');
    try {
      const isPrepayment = createPayload?.prepayment_amount != null;

      const quoteData = {
        supplier: createPayload.supplier,
        corporate_demand_ref: createPayload.corporate_demand_ref,
        budget: createPayload.budget ?? null,
        funding_credit_id: createPayload.funding_credit_id ?? null,
        funding_amount: createPayload.funding_amount ?? null,
      };
      if (createPayload.project != null) {
        quoteData.project = createPayload.project;
      }
      if (createPayload.user != null) {
        quoteData.user = createPayload.user;
      }

      let quoteFileForCreate = null;
      if (isPrepayment) {
        quoteData.prepayment_amount = createPayload.prepayment_amount;
        quoteData.prepayment_currency = createPayload.prepayment_currency;
        quoteData.items = JSON.stringify([]);
        quoteFileForCreate = ocrSourceFile;
        if (!quoteFileForCreate) {
          setOcrSubmitError(
            'Quote PDF is required. Go back and upload the file again.'
          );
          setIsSubmitting(false);
          return;
        }
        quoteData.quote_file_type = quoteFileForCreate.type;
      } else {
        quoteData.items = JSON.stringify(createPayload.items || []);
      }

      logInfo(LOG_CONTEXT, 'OCR review create start', {
        mode: isPrepayment ? 'prepayment' : 'products',
        hasFile: Boolean(quoteFileForCreate),
        quoteFileType: quoteData.quote_file_type ?? null,
        itemCount: isPrepayment
          ? 0
          : Array.isArray(createPayload?.items)
            ? createPayload.items.length
            : 0,
      });
      if (!isPrepayment) {
        logInfo(LOG_CONTEXT, 'OCR review discount snapshot', {
          items: Array.isArray(createPayload?.items)
            ? createPayload.items.map((item, index) => ({
                index,
                price: item?.price,
                discount: item?.discount,
                discount_type: item?.discount_type,
                price_after_discount: item?.price_after_discount,
              }))
            : [],
        });
      }
      const result = await createQuoteManually(
        token,
        quoteData,
        quoteFileForCreate
      );
      if (result && result.success) {
        logInfo(LOG_CONTEXT, 'OCR review create success', {
          mode: isPrepayment ? 'prepayment' : 'products',
          hasFile: Boolean(quoteFileForCreate),
        });
        invalidateSupplierSelectCache();
        invalidateProductSelectCache();
        if (showModal) {
          fetchSupplierSelectList();
        }
        if (onSuccessfulSubmit) {
          onSuccessfulSubmit();
        }
        if (result.toast) result.toast();
        handleClose();
      } else {
        const errMessage =
          (Array.isArray(result?.error) ? result.error.join('\n') : null) ||
          result?.error?.message ||
          result?.message ||
          'Failed to create quote from OCR review.';
        logError(LOG_CONTEXT, 'OCR review create failed', {
          mode: isPrepayment ? 'prepayment' : 'products',
          message: errMessage,
        });
        setOcrSubmitError(String(errMessage));
      }
    } catch (error) {
      logError(LOG_CONTEXT, 'OCR review create error', {
        message: error?.message || null,
      });
      setOcrSubmitError(
        error?.message || 'Failed to create quote from OCR review.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handles the logic for showing the modal
  const handleShow = () => {
    setShowModal(true);
  };

  // Handles changes to the file input field
  const handleFileChange = file => {
    // Sets the selected file to the quoteFile state
    setQuoteFile(file);
    // Indicates that the file has been changed
    setFileChanged(true);
  };

  // Adds a new quote item to the items array
  const addItem = e => {
    e.preventDefault();
    // Appends a new item object to the items array
    setItems([
      ...items,
      {
        product: '',
        quantity: '',
        price: '',
        discount: '',
        currency: uniformCurrencyValue,
      },
    ]);
  };

  // Updates a specific item in the items array
  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  // Removes an item from the items array
  const removeItem = (e, index) => {
    e.preventDefault();
    if (items.length === 1) {
      return; // Prevents removing the last item
    }
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  // Function handling the switch displaying the uniform currency select list
  const handleUniformCurrencySwitch = () => {
    if (uniformCurrencyChecked) {
      if (!quoteObj) {
        setIsUniformCurrency(false);
        setUniformCurrencyValue('');
      }
    }
    setUniformCurrencyChecked(!uniformCurrencyChecked);
  };

  // Function handling the change in value via the uniform currency select list
  const handleUniformCurrencySelectChange = value => {
    const updatedItems = items.map(item => {
      // Create a new object with updated currency
      return {
        ...item,
        currency: value,
      };
    });
    setItems(updatedItems);
    setUniformCurrencyValue(value);
  };

  // Handles the submission of the form
  const handleSubmit = values => {
    setIsSubmitting(true); // Indicates the start of the submission process

    // Prepares the data for submission
    const isPrepayment = values.quoteType === 'prepayment';

    const normalizedItems = isPrepayment
      ? []
      : items.map(item => ({
          ...item,
          quantity:
            parseNumericInput(item.quantity, { integerOnly: true }) ??
            item.quantity,
          price:
            parseNumericInput(item.price) != null
              ? String(parseNumericInput(item.price))
              : item.price,
          discount:
            item.discount === '' || item.discount == null
              ? item.discount
              : parseNumericInput(item.discount) ?? item.discount,
        }));

    const fundingResult = buildQuoteFundingFields({
      budgetLineId: values.budgetLineId,
      budget: values.budget,
      fundingCreditId: values.fundingCreditId,
      items: normalizedItems,
      isPrepayment,
      prepaymentAmount: values.prepaymentAmount,
    });
    if (!fundingResult.ok) {
      showToast(fundingResult.error, 'error', 'top-right', 5000);
      setIsSubmitting(false);
      return;
    }

    const quoteData = {
      supplier: values.supplier,
      items: JSON.stringify(normalizedItems),
      corporate_demand_ref: values.demandRef,
      ...fundingResult.fields,
    };

    if (isPrepayment) {
      const prepaymentParsed = parseNumericInput(values.prepaymentAmount);
      quoteData.prepayment_amount =
        prepaymentParsed != null ? String(prepaymentParsed) : values.prepaymentAmount;
      quoteData.prepayment_currency = values.prepaymentCurrency;
    }

    // Include project association - use projectId prop if provided, otherwise use selectedProject from dropdown
    const projectToUse =
      resolveEntityId(projectId) ??
      (selectedProject != null && String(selectedProject).trim() !== ''
        ? selectedProject
        : null);
    if (quoteObj) {
      quoteData.project_id = projectToUse;
    } else if (projectToUse) {
      quoteData.project = projectToUse;
    }

    // Include user association for notifications
    if (selectedUser) {
      quoteData.user = selectedUser;
    }

    if (quoteFile && fileChanged) {
      quoteData.quote_file_type = quoteFile.type;
    }

    logInfo(LOG_CONTEXT, 'Manual quote submit', {
      mode: quoteObj ? 'edit' : 'create',
      quoteId: quoteObj?.id ?? null,
      quoteType: values.quoteType,
      itemCount: normalizedItems.length,
      hasNewFile: Boolean(quoteFile && fileChanged),
      quoteFileType: quoteData.quote_file_type ?? null,
      fundingSource: fundingResult.fields.funding_credit_id
        ? 'supplier_credit'
        : 'budget',
    });

    // Determines whether to create or update a quote
    const quotePromise = quoteObj
      ? updateQuote(
          token,
          quoteObj.id,
          quoteData,
          fileChanged ? quoteFile : null
        )
      : createQuoteManually(token, quoteData, quoteFile);

    // Processes the promise returned from quote creation or update
    quotePromise.then(response => {
      if (response && response.success) {
        setTimeout(() => {
          if (quoteObj) {
            setQuotes(
              updateObjectInObjectArray(
                quotes,
                quoteObj.id,
                response.updatedQuote
              )
            );
            if (onSuccessfulSubmit) onSuccessfulSubmit();
          } else {
            if (onSuccessfulSubmit) onSuccessfulSubmit();
            if (clearSearchValue) clearSearchValue();
          }

          // Refresh project data when quote is linked to a project
          if (
            (resolveEntityId(projectId) != null || selectedProject) &&
            onProjectUpdate
          ) {
            onProjectUpdate();
          }

          response.toast();
          setIsSubmitting(false);
          handleClose();
          if (!quoteObj) {
            resetModal();
          }
        }, 1000);
      } else {
        showToast(
          formatQuoteApiErrorMessage(response?.error) ||
            'An unexpected error occurred. Please try again in a little while.',
          'error',
          'top-right',
          5000
        );
        setIsSubmitting(false);
      }
    });
  };

  // A function called upon to render editing button
  // If disableEdit is true, button is rendered disabled with an Overlay trigger
  const renderButton = () => {
    // Tooltip rendering function
    const renderTooltip = props => (
      <Tooltip id={`tooltip-delete-${quoteObj.id}`} {...props}>
        Cannot edit: Quote linked to an order.
      </Tooltip>
    );

    if (disableEdit) {
      return (
        <OverlayTrigger
          overlay={renderTooltip}
          placement="top"
          delay={{ show: 50, hide: 400 }}
        >
          <span className="d-inline-block">
            <Button
              variant={quoteObj ? 'outline-success' : 'success'}
              onClick={handleShow}
              onMouseEnter={() => {
                fetchSupplierSelectList();
                fetchProductsOnHover();
              }}
              disabled
              style={{ pointerEvents: 'none' }}
            >
              {quoteObj ? <PencilFill /> : 'Create Quote'}
            </Button>
          </span>
        </OverlayTrigger>
      );
    } else {
      return (
        <Button
          variant={quoteObj ? 'outline-success' : 'success'}
          onClick={handleShow}
          onMouseEnter={() => {
            fetchSupplierSelectList();
            fetchProductsOnHover();
            if (onEditButtonHover) {
              onEditButtonHover();
            }
          }}
        >
          {quoteObj ? <PencilFill /> : 'Create Quote'}
        </Button>
      );
    }
  };

  return (
    <>
      {/* Button to trigger the modal - conditional render based on quoteObj existence */}
      {!homeShowModal && renderButton()}

      {/* Main Modal Component */}
      <Modal
        show={showModal}
        onHide={handleClose}
        backdrop="static"
        size="xl"
        dialogClassName="procure-modal-wide procure-modal-glass"
      >
        <Modal.Header closeButton className="procure-modal__header">
          {/* Dynamic Modal Title based on whether creating a new quote or editing an existing one */}
          <Modal.Title>
            {quoteObj ? `Edit Quote #${quoteObj.id}` : 'Create Quote'}
          </Modal.Title>
        </Modal.Header>

        {/* Show loading spinner when editing and data is being fetched */}
        {quoteObj && isEditButtonLoading ? (
          <Modal.Body
            className="procure-modal__body d-flex justify-content-center align-items-center"
            style={{ minHeight: '200px' }}
          >
            <div className="text-center">
              <Spinner animation="border" size="lg" className="mb-3" />
              <div className="text-muted">Loading quote details...</div>
            </div>
          </Modal.Body>
        ) : (
          /* Formik Component for form handling */
          <Formik
            key={quoteObj ? `edit-${quoteObj.id}` : 'create'}
            enableReinitialize
            // Formik configurations like initial values, validation schema, etc.
            initialTouched={
              quoteObj && quoteObj.items
                ? {
                    items: quoteObj.items.map(() => ({
                      quantity: true,
                      price: true,
                      currency: true,
                    })),
                    supplier: true,
                    quoteFile: true,
                    demandRef: true,
                    budget: true,
                  }
                : {}
            }
            initialValues={{
              items: initialItemsRef.current,
              // Derive initial supplier only from quoteObj (edit mode); for new quotes leave empty.
              // This keeps Formik initialValues stable when the user changes supplier in the UI,
              // so selecting a supplier no longer resets quoteType.
              supplier: quoteObj ? getSupplierIdFromQuote(quoteObj) : '',
              quoteType: isPrepaymentExistingQuote ? 'prepayment' : 'products',
              prepaymentAmount:
                quoteObj && quoteObj.prepayment_amount
                  ? String(quoteObj.prepayment_amount)
                  : '',
              prepaymentCurrency:
                quoteObj && quoteObj.prepayment_currency
                  ? quoteObj.prepayment_currency
                  : '',
              quoteFile: '',
              demandRef: quoteObj ? quoteObj.corporate_demand_ref : '',
              fundingCreditId: quoteObj?.funding_credit
                ? String(
                    typeof quoteObj.funding_credit === 'object'
                      ? quoteObj.funding_credit.id
                      : quoteObj.funding_credit
                  )
                : '',
              budgetLineId: quoteObj?.budget_line?.id ?? '',
              budget: quoteObj ? quoteObj.budget : '',
            }}
            validateOnMount={!!quoteObj}
            validationSchema={formSchema}
            validate={values => {
              const errors = {};
              const hasBudgetLine =
                values.budgetLineId != null && String(values.budgetLineId).trim() !== '';
              const budgetText = String(values.budget ?? '').trim();
              const hasCredit =
                String(values.fundingCreditId ?? '').trim() !== '';
              if (!hasBudgetLine && !budgetText && !hasCredit) {
                errors.budgetLineId = QUOTE_FUNDING_REQUIRED_MESSAGE;
                return errors;
              }
              if (!hasBudgetLine && !budgetText && hasCredit) {
                const isPrepayment = values.quoteType === 'prepayment';
                // Formik values, not `items`: line edits reach `items` only after
                // QuoteItemComponent's 300 ms debounce.
                const normalizedItems = isPrepayment
                  ? []
                  : (values.items || []).map(item => ({
                      quantity: item?.quantity,
                      price: item?.price,
                      discount: item?.discount,
                    }));
                if (
                  !hasValidQuoteCreditFunding({
                    fundingCreditId: values.fundingCreditId,
                    items: normalizedItems,
                    isPrepayment,
                    prepaymentAmount: values.prepaymentAmount,
                  })
                ) {
                  errors.budget = isPrepayment
                    ? 'Enter a prepayment amount when using a supplier credit.'
                    : 'Add line items with quantity and price when using a supplier credit.';
                }
              }
              return errors;
            }}
            onSubmit={values => {
              handleSubmit(values);
            }}
          >
            {({
              // Destructuring useful properties and functions from Formik
              handleChange,
              handleSubmit,
              values,
              handleBlur,
              touched,
              errors,
              isValid,
              setFieldTouched,
              dirty,
              setFieldValue,
            }) => {
              const isEditMode = !!quoteObj;
              const isWizardCreate = !quoteObj;
              /** @type {'type'|'method'|'form'} */
              const wizardStage = isEditMode
                ? 'form'
                : !quoteTypeChosen
                  ? 'type'
                  : entryMethod == null
                    ? 'method'
                    : 'form';
              const showMainFormFields =
                isEditMode ||
                (isWizardCreate &&
                  wizardStage === 'form' &&
                  entryMethod === 'manual');
              const showPrepaymentFileUpload =
                isWizardCreate &&
                wizardStage === 'form' &&
                entryMethod === 'file' &&
                values.quoteType === 'prepayment';
              const showProductsFileUpload =
                isWizardCreate &&
                wizardStage === 'form' &&
                entryMethod === 'file' &&
                values.quoteType === 'products';
              const hideFooterCreate =
                (isWizardCreate && wizardStage !== 'form') ||
                showProductsFileUpload ||
                showPrepaymentFileUpload ||
                !!ocrReviewData;
              const showFundingSection =
                (values.supplier || supplier) &&
                (values.quoteType === 'products' ||
                  values.quoteType === 'prepayment') &&
                showMainFormFields;

              return (
                <Form noValidate onSubmit={handleSubmit}>
                  <RevalidateAfterValuesChange />
                  <Modal.Body className="procure-modal__body d-flex flex-column">
                    {isWizardCreate && wizardStage === 'type' && (
                      <QuoteWizardPanel
                        step="Step 1"
                        title="Choose quote type"
                        subtitle="Pick the kind of quote you are creating. You can change the entry method on the next step."
                      >
                        <Row className="g-3">
                          <Col md={6}>
                            <QuoteWizardChoice
                              icon={BoxSeam}
                              title="Products quote"
                              description="Line items, supplier catalog, and quote file."
                              onClick={() => {
                                setFieldValue('quoteType', 'products');
                                setQuoteTypeChosen(true);
                                setEntryMethod(null);
                              }}
                              disabled={disableEdit}
                            />
                          </Col>
                          <Col md={6}>
                            <QuoteWizardChoice
                              icon={Wallet2}
                              title="Prepayment quote"
                              description="Prepayment amount and currency (no product lines)."
                              onClick={() => {
                                setFieldValue('quoteType', 'prepayment');
                                setQuoteTypeChosen(true);
                                setEntryMethod(null);
                              }}
                              disabled={disableEdit}
                            />
                          </Col>
                        </Row>
                      </QuoteWizardPanel>
                    )}

                    {isWizardCreate && wizardStage === 'method' && (
                      <QuoteWizardPanel
                        step="Step 2"
                        title="How do you want to create this quote?"
                        subtitle={
                          values.quoteType === 'prepayment'
                            ? 'Manual entry or upload a supplier PDF for prepayment.'
                            : 'Manual entry, supplier PDF extraction, Excel requisitions, or Sigma order confirmation CSV.'
                        }
                        footer={
                          <QuoteWizardBackButton
                            label="← Back to quote type"
                            onClick={() => {
                              setQuoteTypeChosen(false);
                              setEntryMethod(null);
                            }}
                          />
                        }
                      >
                        <Row className="g-3">
                          <Col md={6}>
                            <QuoteWizardChoice
                              icon={PencilFill}
                              title="Manual entry"
                              description="Select supplier and fill the form yourself."
                              selected={entryMethod === 'manual'}
                              onClick={() => setEntryMethod('manual')}
                              disabled={disableEdit}
                            />
                          </Col>
                          <Col md={6}>
                            <QuoteWizardChoice
                              icon={FiletypePdf}
                              title="From file"
                              description={
                                values.quoteType === 'prepayment'
                                  ? 'Upload one supplier quote PDF for server extraction and review.'
                                  : 'Supplier quote PDF, Excel requisitions, or Sigma order confirmation CSV.'
                              }
                              selected={entryMethod === 'file'}
                              onClick={() => setEntryMethod('file')}
                              disabled={disableEdit}
                            />
                          </Col>
                        </Row>
                      </QuoteWizardPanel>
                    )}

                    {showMainFormFields && (
                      <>
                        {isWizardCreate && entryMethod === 'manual' && (
                          <div className="mb-3">
                            <QuoteWizardBackButton
                              label="← Back to entry method"
                              onClick={() => setEntryMethod(null)}
                            />
                          </div>
                        )}
                    <Row className="align-items-start">
                      {/* Form Group for Supplier Selection */}
                      <Col md={showFundingSection ? 6 : 8}>
                        <Form.Group
                          controlId="selectSupplier"
                          className="field-margin mb-3"
                        >
                          <Form.Label>Select Supplier</Form.Label>
                          {/* Dropdown for selecting supplier */}
                          {isLoadingSuppliers &&
                          localSupplierSelectList.length === 0 ? (
                            <div className="d-flex align-items-center">
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                              />
                              <span className="text-muted">
                                Loading suppliers...
                              </span>
                            </div>
                          ) : (
                            <DropdownSelect
                              optionsList={localSupplierSelectList}
                              label="Supplier"
                              selectedValue={
                                localSupplierSelectList.find(
                                  p =>
                                    p.value === values.supplier ||
                                    String(p.value) === String(values.supplier)
                                ) || null
                              }
                              setSelectedValue={option => {
                                const value = option?.value || '';
                                setFieldValue('supplier', value);
                                setSupplier(value);
                              }}
                              touched={touched.supplier}
                              error={errors.supplier}
                              isInvalid={!!errors.supplier}
                              onBlur={() => setFieldTouched('supplier', true)}
                              disabledOptions={[]}
                            />
                          )}
                          <Form.Control.Feedback type="invalid">
                            {errors.supplier}
                          </Form.Control.Feedback>
                          {!supplier && (
                            <Form.Text>
                              Choose a supplier to view it's products
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>

                      {showFundingSection && (
                        <Col md={6}>
                          <div className="field-margin mb-3 funding-source-panel">
                            <Form.Label className="mb-2 funding-source-title small text-muted">
                              Funding source <span className="text-danger ms-1">*</span>
                            </Form.Label>

                            {/* Supplier credit select (optional alternative to budget) */}
                            <Form.Group
                              controlId="fundingCreditId"
                              className="mb-2"
                            >
                              <Form.Label className="mb-1">
                                Supplier credit (optional)
                              </Form.Label>
                              {isLoadingSupplierCredits ? (
                                <div className="d-flex align-items-center">
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                  />
                                  <span className="text-muted">
                                    Loading credits...
                                  </span>
                                </div>
                              ) : (
                                <Form.Select
                                  name="fundingCreditId"
                                  value={values.fundingCreditId || ''}
                                  onChange={e => {
                                    const nextCredit = e.target.value || '';
                                    setFieldValue('fundingCreditId', nextCredit);
                                    if (nextCredit) {
                                      setFieldValue('budget', '');
                                      setFieldValue('budgetLineId', '');
                                    }
                                  }}
                                  onBlur={handleBlur}
                                >
                                  <option value="">
                                    None / use Budget
                                  </option>
                                  {supplierCredits.map(credit => {
                                    return (
                                      <option key={credit.id} value={credit.id}>
                                        {credit.name ||
                                          `Credit #${credit.id}`} – remaining{' '}
                                        {credit.remaining_amount}{' '}
                                        {credit.currency}
                                      </option>
                                    );
                                  })}
                                </Form.Select>
                              )}
                            </Form.Group>
                            {values.fundingCreditId ? (
                              <Form.Text className="text-muted d-block mb-2">
                                Amount charged to credit is calculated from line
                                items.
                              </Form.Text>
                            ) : null}
                            {errors.budget ? (
                              <Form.Control.Feedback
                                type="invalid"
                                className="d-block mb-2"
                              >
                                {errors.budget}
                              </Form.Control.Feedback>
                            ) : null}
                            <Form.Group controlId="budgetLineId" className="mb-0">
                              <BudgetLineSelect
                                token={token}
                                label="Budget"
                                required
                                value={values.budgetLineId || null}
                                onChange={id => {
                                  setFieldValue('budgetLineId', id ?? '');
                                  if (id) {
                                    setFieldValue('budget', '');
                                    setFieldValue('fundingCreditId', '');
                                  }
                                }}
                                error={touched.budgetLineId ? errors.budgetLineId : ''}
                              />
                              <Form.Control.Feedback type="invalid" className="d-block">
                                {errors.budgetLineId}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </div>
                        </Col>
                      )}
                    </Row>
                    {/* Quote type (edit mode only; create flow uses wizard above) */}
                    {quoteObj && (
                    <Form.Group className="mb-3">
                      <Form.Label>Quote type</Form.Label>
                      <div className="d-flex flex-row gap-3">
                        <Form.Check
                          type="radio"
                          id="quoteType-products"
                          label="Products quote"
                          name="quoteType"
                          value="products"
                          checked={values.quoteType === 'products'}
                          onChange={handleChange}
                        />
                        <Form.Check
                          type="radio"
                          id="quoteType-prepayment"
                          label="Prepayment quote"
                          name="quoteType"
                          value="prepayment"
                          checked={values.quoteType === 'prepayment'}
                          onChange={handleChange}
                        />
                      </div>
                    </Form.Group>
                    )}

                    {/* Prepayment fields */}
                    {values.quoteType === 'prepayment' && (
                      <Row className="mb-3">
                        <Form.Group
                          as={Col}
                          md="4"
                          className="field-margin"
                          controlId="prepaymentAmount"
                        >
                          <Form.Label>
                            Prepayment amount <RequiredAsteriskComponent />
                          </Form.Label>
                          <NumberInput
                            name="prepaymentAmount"
                            value={values.prepaymentAmount}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={
                              touched.prepaymentAmount &&
                              !!errors.prepaymentAmount
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.prepaymentAmount}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group
                          as={Col}
                          md="4"
                          className="field-margin"
                          controlId="prepaymentCurrency"
                        >
                          <Form.Label>
                            Prepayment currency <RequiredAsteriskComponent />
                          </Form.Label>
                          <Form.Select
                            name="prepaymentCurrency"
                            value={values.prepaymentCurrency}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={
                              touched.prepaymentCurrency &&
                              !!errors.prepaymentCurrency
                            }
                          >
                            <option value="">-- Select currency --</option>
                            <option value="NIS">NIS</option>
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors.prepaymentCurrency}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Row>
                    )}

                    {supplier && values.quoteType === 'products' && (
                      <Form.Group
                        className="mb-1 d-flex flex-row "
                        controlId="uniformCurrencySwitch"
                      >
                        <Form.Switch
                          checked={uniformCurrencyChecked}
                          onChange={handleUniformCurrencySwitch}
                          className={'me-2'}
                        />
                        <Form.Label>Same currency for all items?</Form.Label>
                      </Form.Group>
                    )}
                    {uniformCurrencyChecked &&
                      supplier &&
                      values.quoteType === 'products' && (
                      <Form.Group controlId="uniformCurrency">
                        <Form.Label>
                          Uniform Quote Currency <RequiredAsteriskComponent />
                        </Form.Label>
                        {/* Input for measurement unit with validation feedback. */}
                        <Form.Select
                          value={uniformCurrencyValue}
                          onChange={event =>
                            handleUniformCurrencySelectChange(
                              event.target.value
                            )
                          }
                          isValid={
                            uniformCurrencyValue && uniformCurrencyChecked
                          }
                        >
                          <option value="" disabled>
                            -- Select currency --
                          </option>
                          <option value="NIS">NIS</option>
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                        </Form.Select>
                      </Form.Group>
                    )}
                    {/* Conditional rendering for displaying quote items (products quotes only) */}
                    {values.quoteType === 'products' &&
                    (quoteObj || entryMethod === 'manual') &&
                    items.length > 0 &&
                    productSelectList.length > 0 ? (
                      <div>
                        <div>
                          {/* Mapping and rendering each QuoteItemComponent */}
                          {items.map((item, index) => (
                            <QuoteItemComponent
                              key={index}
                              editMode={!!quoteObj}
                              productList={productSelectList}
                              onItemChange={updateItem}
                              index={index}
                              item={item.product}
                              productRecord={quoteObj?.items?.[index]?.product}
                              disabledOptions={items.map(item => item.product)}
                              handleItemDelete={removeItem}
                              showRemoveButton={items.length === 1}
                              isUniformCurrency={isUniformCurrency}
                              uniformCurrencyValue={uniformCurrencyValue}
                              formik={{
                                handleChange,
                                values,
                                handleBlur,
                                touched,
                                errors,
                                setFieldTouched,
                                setFieldValue,
                              }}
                            />
                          ))}
                        </div>
                        {/* Button to add a new quote item */}
                        <Button
                          className="field-margin"
                          aria-label="Add item"
                          onClick={e => {
                            addItem(e);
                          }}
                          variant="outline-success"
                        >
                          <Plus size={30} />
                        </Button>
                      </div>
                    ) : values.quoteType === 'prepayment' &&
                      supplier &&
                      (quoteObj || entryMethod === 'manual') ? (
                      <p className="text-muted small mb-0">
                        Prepayment quote – no products needed.
                      </p>
                    ) : supplier ? (
                      fetchingProductsRef.current ? (
                        <Spinner size={'sm'} variant={'dark'} />
                      ) : (
                        <h6>This supplier has no products related to it</h6>
                      )
                    ) : null}

                    {/* Shared: optional quote file, demandRef, project, user */}
                    {((values.quoteType === 'products' &&
                      items.length > 0 &&
                      productSelectList.length > 0 &&
                      (quoteObj || entryMethod === 'manual')) ||
                      (values.quoteType === 'prepayment' &&
                        supplier &&
                        (quoteObj || entryMethod === 'manual'))) && (
                      <div className="mt-3">
                        <Form.Group
                          controlId="formOrderImages"
                          className="field-margin"
                        >
                          <Form.Label>
                            Upload Quote File (PDF, Word, Excel)
                            <span className="text-muted small"> (Optional)</span>
                          </Form.Label>
                          <Form.Control
                            type="file"
                            accept="application/pdf,
                                  application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                                  application/vnd.ms-excel,
                                  application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            name="quoteFile"
                            onChange={event => {
                              const files = Array.from(event.target.files);
                              handleFileChange(files[0]);
                              setFieldValue('quoteFile', files);
                            }}
                            onBlur={handleBlur}
                            isValid={!errors.quoteFile && values.quoteFile}
                            isInvalid={touched.quoteFile && !!errors.quoteFile}
                          />
                          <Form.Control.Feedback type="valid">
                            Looks good!
                          </Form.Control.Feedback>
                          <Form.Control.Feedback type="invalid">
                            {errors.quoteFile}
                          </Form.Control.Feedback>
                        </Form.Group>
                        {quoteFile && (
                          <div className="field-margin">
                            {quoteFile &&
                              (() => {
                                let fileUrl;
                                let isPdf;
                                const file =
                                  Array.isArray(quoteFile) ? quoteFile[0] : quoteFile;
                                if (file instanceof Blob) {
                                  fileUrl = URL.createObjectURL(file);
                                  isPdf = file.type === 'application/pdf';
                                } else if (typeof file === 'string') {
                                  fileUrl = file;
                                  isPdf = file.toLowerCase().endsWith('.pdf');
                                } else if (quoteFile instanceof Blob) {
                                  fileUrl = URL.createObjectURL(quoteFile);
                                  isPdf = quoteFile.type === 'application/pdf';
                                } else if (typeof quoteFile === 'string') {
                                  fileUrl = quoteFile;
                                  isPdf = quoteFile
                                    .toLowerCase()
                                    .endsWith('.pdf');
                                }
                                return fileUrl ? (
                                  <div className="mt-2">
                                    {isPdf ? (
                                      <a
                                        href={fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-outline-dark align-middle p-3 me-3"
                                      >
                                        <FiletypePdf size={30} />
                                      </a>
                                    ) : (
                                      <a
                                        href={fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <img
                                          src={fileUrl}
                                          alt="quote-file"
                                          width="200"
                                          className="me-3"
                                        />
                                      </a>
                                    )}
                                  </div>
                                ) : null;
                              })()}
                          </div>
                        )}
                        <Form.Group
                          controlId="demandRef"
                          className="field-margin"
                        >
                          <Form.Label className="mt-3">
                            Demand Reference <RequiredAsteriskComponent />
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="demandRef"
                            value={values.demandRef}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isValid={!errors?.demandRef && values?.demandRef}
                            isInvalid={
                              (touched?.demandRef && !values?.demandRef) ||
                              (errors?.demandRef &&
                                errors?.demandRef !==
                                  'Demand reference is required' &&
                                values?.demandRef)
                            }
                          />
                          <Form.Control.Feedback type="valid">
                            Looks good!
                          </Form.Control.Feedback>
                          <Form.Control.Feedback type="invalid">
                            {errors.demandRef}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Row>
                          <Col md={6}>
                            <Form.Group
                              controlId="project"
                              className="field-margin"
                            >
                              <Form.Label>Project (Optional)</Form.Label>
                              {isLoadingProjects ? (
                                <div className="d-flex align-items-center">
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                  />
                                  <span className="text-muted">
                                    Loading projects...
                                  </span>
                                </div>
                              ) : (
                                <DropdownSelect
                                  optionsList={projectSelectList}
                                  label="Project"
                                  selectedValue={
                                    projectSelectList.find(
                                      p => p.value === selectedProject
                                    ) || null
                                  }
                                  setSelectedValue={option => {
                                    setSelectedProject(option?.value || null);
                                  }}
                                  placeholder="Select project (optional)"
                                  disabledOptions={[]}
                                />
                              )}
                              <Form.Text className="text-muted">
                                Link this quote to a project to automatically
                                update project statuses
                              </Form.Text>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group
                              controlId="user"
                              className="field-margin"
                            >
                              <Form.Label>User (Optional)</Form.Label>
                              {isLoadingUsers ? (
                                <div className="d-flex align-items-center">
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                  />
                                  <span className="text-muted">
                                    Loading users...
                                  </span>
                                </div>
                              ) : (
                                <DropdownSelect
                                  optionsList={userDropdownOptions}
                                  label="User"
                                  selectedValue={findUserOption(
                                    userDropdownOptions,
                                    selectedUser
                                  )}
                                  setSelectedValue={option => {
                                    const v = option?.value;
                                    setSelectedUser(
                                      option == null || v === '' ? null : v
                                    );
                                  }}
                                  placeholder="Select user (optional)"
                                  disabledOptions={[]}
                                />
                              )}
                              <Form.Text className="text-muted">
                                Select a user to notify when products arrive.
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    )}
                    </>
                    )}

                    {/* Prepayment + from file: PDF upload → OCR parse → review (same UX as products) */}
                    {showPrepaymentFileUpload && (
                      <div className="procure-file-flow-panel">
                        <QuoteWizardPanel
                          step="Step 3"
                          title="Upload prepayment quote PDF"
                          subtitle="Select a supplier PDF. We will extract details for your review before creating the quote."
                          footer={
                            <QuoteWizardBackButton
                              label="← Back to entry method"
                              onClick={() => {
                                setEntryMethod(null);
                                setOcrReviewData(null);
                                setOcrSourceFile(null);
                                setOcrParseError('');
                                setOcrSubmitError('');
                              }}
                            />
                          }
                        >
                        {ocrReviewData ? (
                          <QuoteOcrReviewStep
                            reviewData={ocrReviewData}
                            token={token}
                            supplierOptions={localSupplierSelectList}
                            projectOptions={projectSelectList}
                            userOptions={userDropdownOptions}
                            isLoadingProjects={isLoadingProjects}
                            isLoadingUsers={isLoadingUsers}
                            selectedProject={selectedProject}
                            selectedUser={selectedUser}
                            onProjectChange={setSelectedProject}
                            onUserChange={setSelectedUser}
                            onCancel={() => {
                              setOcrReviewData(null);
                              setOcrSourceFile(null);
                              setOcrSubmitError('');
                              setIsOcrParsing(false);
                            }}
                            onConfirm={handleConfirmOcrReview}
                            isSubmitting={isSubmitting}
                            submitError={ocrSubmitError}
                            disabled={disableEdit}
                          />
                        ) : null}

                        {!ocrReviewData && (
                          <>
                            {ocrParseError && (
                              <Alert variant="danger" className="mt-2 mb-3 py-2 small">
                                {ocrParseError}
                              </Alert>
                            )}
                            <QuotePdfUploadComponent
                              prepaymentOcrMode
                              onPrepaymentOcrHandoff={handlePrepaymentOcrHandoff}
                              onPdfProcessed={() => {}}
                              disabled={disableEdit || isOcrParsing}
                              token={token}
                              isProcessingExternally={isOcrParsing}
                            />
                          </>
                        )}
                        </QuoteWizardPanel>
                      </div>
                    )}
                    {/* Products: supplier PDF extract/review + Excel bulk */}
                    {showProductsFileUpload && (
                      <div className="procure-file-flow-panel">
                        <QuoteWizardPanel
                          step="Step 3"
                          title="Create quote from file"
                          subtitle="Upload a supplier PDF for extraction, Excel requisitions, or a Sigma order confirmation CSV."
                          footer={
                            <QuoteWizardBackButton
                              label="← Back to entry method"
                              onClick={() => setEntryMethod(null)}
                            />
                          }
                        >
                        <>
                            <QuotePdfUploadComponent
                              onPdfProcessed={result => {
                                const hadCreated = (result.totalCreated ?? 0) > 0;
                                if (hadCreated) {
                                  invalidateSupplierSelectCache();
                                  invalidateProductSelectCache();
                                  if (showModal) {
                                    fetchSupplierSelectList();
                                  }
                                  if (onSuccessfulSubmit) {
                                    onSuccessfulSubmit();
                                  }
                                  handleClose();
                                }
                              }}
                              disabled={disableEdit}
                              token={token}
                              supplierOptions={localSupplierSelectList}
                              projectOptions={projectSelectList}
                              userOptions={userDropdownOptions}
                              selectedProject={selectedProject}
                              selectedUser={selectedUser}
                              onProjectChange={setSelectedProject}
                              onUserChange={setSelectedUser}
                            />
                            <QuoteExcelUploadComponent
                              onExcelProcessed={result => {
                                const hadCreated = (result.totalCreated ?? 0) > 0;
                                if (hadCreated) {
                                  invalidateSupplierSelectCache();
                                  invalidateProductSelectCache();
                                  if (showModal) {
                                    fetchSupplierSelectList();
                                  }
                                  if (onSuccessfulSubmit) {
                                    onSuccessfulSubmit();
                                  }
                                  if (
                                    (resolveEntityId(projectId) != null ||
                                      selectedProject) &&
                                    onProjectUpdate
                                  ) {
                                    onProjectUpdate();
                                  }
                                  handleClose();
                                }
                              }}
                              onOrderConfirmationSaved={result => {
                                if (result?.success) {
                                  invalidateSupplierSelectCache();
                                  invalidateProductSelectCache();
                                  if (showModal) {
                                    fetchSupplierSelectList();
                                  }
                                  if (onSuccessfulSubmit) {
                                    onSuccessfulSubmit();
                                  }
                                  if (
                                    (resolveEntityId(projectId) != null ||
                                      selectedProject) &&
                                    onProjectUpdate
                                  ) {
                                    onProjectUpdate();
                                  }
                                  handleClose();
                                }
                              }}
                              disabled={disableEdit}
                              token={token}
                              supplierOptions={localSupplierSelectList}
                              projectOptions={projectSelectList}
                              userOptions={userDropdownOptions}
                              selectedProject={selectedProject}
                              selectedUser={selectedUser}
                              onProjectChange={setSelectedProject}
                              onUserChange={setSelectedUser}
                            />
                        </>
                        </QuoteWizardPanel>
                      </div>
                    )}
                  </Modal.Body>
                  <Modal.Footer className="procure-modal__footer">
                    {/* Submit and close buttons with conditional rendering and event handlers */}
                    {hideFooterCreate ? null : isSubmitting ? (
                      <Button variant="primary" className="glass-primary-btn" disabled>
                        <Spinner
                          size="sm"
                          as="span"
                          animation="border"
                          role="status"
                          aria-hidden="true"
                        />
                      </Button>
                    ) : (
                      <Button
                        variant="primary"
                        className="glass-primary-btn"
                        disabled={
                          values.quoteType === 'prepayment'
                            ? !isValid
                            : !isValid ||
                              !itemsHaveProducts ||
                              (!quoteObj && !dirty && !itemsHaveProducts)
                        }
                        onClick={handleSubmit}
                      >
                        {quoteObj ? 'Save' : 'Create'}
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      className="procure-modal__close-btn"
                      onClick={handleClose}
                    >
                      Close
                    </Button>
                  </Modal.Footer>
                </Form>
              );
            }}
          </Formik>
        )}
      </Modal>
    </>
  );
};
export default QuoteModal;
